import type { LearnerState } from '@/types/learner';
import { PERSONAS_SEED } from '@/seed/personas';
import { MILESTONES } from '@/seed/milestones';
import { COACH } from '@/constants/coach';

/**
 * Compose the coach's dashboard greeting. Per brief §4.1, reads from the most
 * recent session and saved_playbook and references a specific accomplishment.
 * Pure function so it can be unit-tested and later swapped for an LLM call.
 */

export interface Greeting {
  eyebrow: string;
  headline: string;
  body: string;
}

export function composeGreeting(learner: LearnerState): Greeting {
  const last = learner.session_history[0];
  const savedCount = learner.saved_playbook.length;

  const persona = last ? PERSONAS_SEED[last.persona_id] : undefined;
  if (!last || !persona) {
    return {
      eyebrow: COACH.title,
      headline: `Welcome, ${firstName(learner.display_name)} — I'm ${COACH.firstName}.`,
      body: "Let me show you around. We'll start with Module 1 and work toward your first roleplay.",
    };
  }

  const firstSaved = learner.saved_playbook[0];

  const { possessive, subject } = persona.pronouns;

  // Specific moments and milestones from the most recent session — anchors
  // the greeting body to what the learner actually did rather than a stock
  // sentence per outcome.
  const winMoment = last.moments.find((m) => m.type === 'win');
  const growthMoment = last.moments.find((m) => m.type === 'growth');
  const milestones = last.milestones_hit;
  const firstMilestone = milestones[0];

  if (last.outcome === 'closed') {
    const winLine = winMoment
      ? `Your strongest moment was when you said: "${shortenQuote(winMoment.quote, 90)}" — that's the kind of question that pulls a number out of an abstraction.`
      : `You named ${possessive} pain in ${possessive} words and then asked for the small yes.`;
    return {
      eyebrow: COACH.title,
      headline: `You closed ${persona.display_name} — the right way.`,
      body: `That wasn't luck. ${winLine} The next persona will push harder — let's get you ready.`,
    };
  }

  // Saved-play branch — only fires if the FIRST saved play is from the
  // same persona as `last`. Mixing them produced sentences like "You didn't
  // close Linda last time — but you saved <Kayla quote>", which read as a
  // mismatch. When the saved play is from a different persona, fall through
  // to the moment/milestone branches below so the body stays anchored to
  // last's actual session.
  const lastPersonaSession = learner.session_history.find((s) => s.id === firstSaved?.session_id);
  const firstSavedIsFromLast = !!firstSaved && lastPersonaSession?.persona_id === last.persona_id;
  if (firstSaved && savedCount >= 1 && firstSavedIsFromLast) {
    const snippet = shortenQuote(firstSaved.quote);
    return {
      eyebrow: COACH.title,
      headline: `Welcome back, ${firstName(learner.display_name)}.`,
      body: `You didn't close ${persona.display_name} last time — but you saved "${snippet}" as your play. That's the right instinct. Lesson ${learner.current_lesson} picks up where ${subject}'d push you next.`,
    };
  }

  // No saved play yet — anchor to whatever the most concrete signal in the
  // session was. Prefer a growth moment (something to fix), then a milestone
  // they hit, then bare score.
  let body: string;
  if (growthMoment) {
    body = `Your last attempt with ${persona.display_name} scored ${last.score}. The thing that lost you the room: ${growthMoment.coach_note} Lesson ${learner.current_lesson} is exactly the fix — let's run it.`;
  } else if (firstMilestone) {
    body = `Your last attempt with ${persona.display_name} scored ${last.score} — and you hit ${labelForMilestone(firstMilestone)}. Lesson ${learner.current_lesson} stacks the next move on top of that. Then you're going to want another try.`;
  } else {
    body = `Your last attempt with ${persona.display_name} scored ${last.score}. We've got the reframe covered in Lesson ${learner.current_lesson} — then you're going to want another try.`;
  }
  return {
    eyebrow: COACH.title,
    headline: `Welcome back, ${firstName(learner.display_name)}.`,
    body,
  };
}

function labelForMilestone(m: import('@/types/session').Milestone): string {
  // Label from the vertical pack (src/seed/milestones). Lowercased so it reads
  // naturally mid-sentence in the coach greeting.
  return MILESTONES[m]?.label.toLowerCase() ?? 'that move';
}

function firstName(displayName: string): string {
  return displayName.split(' ')[0] ?? displayName;
}

function shortenQuote(q: string, limit = 72): string {
  if (q.length <= limit) return q;
  const trimmed = q.slice(0, limit).trimEnd();
  return `${trimmed}…`;
}
